import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Shield, ArrowLeft, Clock, CheckCircle2, Rocket } from 'lucide-react';

const RoadmapPage = () => {
  const navigate = useNavigate();

  const roadmapItems = [
    {
      status: 'In Progress',
      color: 'bg-blue-100 text-blue-700 border-blue-300',
      icon: Rocket,
      items: [
        { title: 'Dark Mode Support', desc: 'Full dark theme for the entire platform', eta: 'Q2 2025' },
        { title: 'Mobile Apps (iOS/Android)', desc: 'Native mobile applications for on-the-go management', eta: 'Q2 2025' },
        { title: 'Advanced Automation Workflows', desc: 'Visual workflow builder with conditional logic', eta: 'Q3 2025' }
      ]
    },
    {
      status: 'Planned',
      color: 'bg-purple-100 text-purple-700 border-purple-300',
      icon: Clock,
      items: [
        { title: 'Multi-Tenant Dashboard', desc: 'Allow clients to view their own assets and tickets', eta: 'Q3 2025' },
        { title: 'AI-Powered Insights', desc: 'Predictive analytics and smart recommendations', eta: 'Q4 2025' },
        { title: 'Backup Management', desc: 'Monitor and manage backups across all endpoints', eta: 'Q4 2025' },
        { title: 'Network Monitoring', desc: 'Monitor switches, routers, and network devices', eta: 'Q1 2026' },
        { title: 'Linux & Mac Support', desc: 'Agent support for Linux and macOS endpoints', eta: 'Q1 2026' }
      ]
    },
    {
      status: 'Recently Released',
      color: 'bg-green-100 text-green-700 border-green-300',
      icon: CheckCircle2,
      items: [
        { title: 'Screenshot Monitoring', desc: 'Automatic screenshot capture for troubleshooting', released: 'January 2025' },
        { title: 'Mobile Responsive Design', desc: 'Fully responsive interface for all devices', released: 'January 2025' },
        { title: 'Settings Reorganization', desc: 'Cleaner, categorized settings pages', released: 'January 2025' },
        { title: 'Agent Auto-Update', desc: 'Automated agent updates with version management', released: 'December 2024' },
        { title: 'Performance Optimization', desc: 'Faster page loads and improved caching', released: 'December 2024' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/public/resources')} className="mr-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">RMM Pro</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Product Roadmap</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            See what we're building and what's coming next. Your feedback helps shape our roadmap.
          </p>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {roadmapItems.map((section, index) => (
              <div key={index}>
                <div className="flex items-center mb-6">
                  <section.icon className="w-6 h-6 mr-3 text-slate-600" />
                  <h2 className="text-2xl font-bold text-slate-900">{section.status}</h2>
                </div>

                <div className="space-y-4">
                  {section.items.map((item, itemIndex) => (
                    <Card key={itemIndex} className="border-slate-200 hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                            <p className="text-slate-600 mb-3">{item.desc}</p>
                          </div>
                          <Badge variant="outline" className={section.color}>
                            {item.eta || item.released}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feedback CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Have a Feature Request?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            We love hearing from our customers. Share your ideas and help shape the future of RMM Pro.
          </p>
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            Submit Feature Request
          </Button>
        </div>
      </section>
    </div>
  );
};

export default RoadmapPage;